import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Email, Phone, Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { changePassword } from "../../service/authService";

const Profile = () => {
  // Read from localStorage
  const name = localStorage.getItem("name") || "User";
  const email = localStorage.getItem("email") || "user@example.com";
  const role = localStorage.getItem("role") || "User";
  const roleName = localStorage.getItem("roleName") || "User";
  const joinedDate = localStorage.getItem("joinedDate") || new Date().toLocaleDateString();
  const mobile = localStorage.getItem("mobile") || "+91 0000000000";
  const avatar = "/profile.png";

  // Hide change password section for Manager (role_id 2) and Kitchen/Factory (role_id 3) roles
  const userRole = parseInt(role);
  const normalizedRoleName = roleName ? roleName.toLowerCase() : "";
  const isManagerOrKitchen = 
    userRole === 2 || 
    userRole === 3 || 
    normalizedRoleName === "manager" || 
    normalizedRoleName === "kitchen" || 
    normalizedRoleName === "kitchen staff" ||
    normalizedRoleName === "factory";

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("All password fields are required");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      if (response.data?.error) {
        if (typeof response.data.error === "object") {
          const errors = Object.values(response.data.error).flat().join(", ");
          toast.error(errors);
        } else {
          toast.error(response.data.error);
        }
      } else {
        toast.success(response.data?.message || "Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      console.error("Change password error:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || "Failed to change password";
      if (typeof errMsg === "object") {
        const errors = Object.values(errMsg).flat().join(", ");
        toast.error(errors);
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        mx: "auto",
        mt: 2,
        width: "100%",
        fontFamily: '"Poppins", sans-serif',
      }}
    >
      {/* Profile Info Card */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 4,
          p: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Avatar
                src={avatar}
                alt={name}
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: 30,
                  bgcolor: "#7C0BCC",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={8}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {name}
              </Typography>

              <Box display="flex" alignItems="center" mb={1}>
                <Email sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body1">{email}</Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <Phone sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body1">{mobile}</Typography>
              </Box>
            </CardContent>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Role
            </Typography>
            <Typography variant="body1">{roleName}</Typography>
          </Grid>

          <Grid item xs={6} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Joined On
            </Typography>
            <Typography variant="body1">{joinedDate}</Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Change Password Card */}
      {!isManagerOrKitchen && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 4,
            p: 3,
            mt: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Lock sx={{ color: "#7C0BCC" }} /> Change Password
          </Typography>
          
          <Divider sx={{ my: 2 }} />

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={12} md={3}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  size="small"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                          {showCurrent ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  size="small"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  size="small"
                  required
                  error={confirmPassword !== "" && newPassword !== confirmPassword}
                  helperText={confirmPassword !== "" && newPassword !== confirmPassword ? "Passwords do not match" : ""}
                  FormHelperTextProps={{
                    style: {
                      position: "absolute",
                      bottom: "-20px",
                      left: 0,
                      margin: 0,
                      fontSize: "10px",
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || (confirmPassword !== "" && newPassword !== confirmPassword)}
                  fullWidth
                  sx={{
                    height: "40px",
                    background: "linear-gradient(45deg, #7C0BCC 30%, #9C27B0 90%)",
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: 1.5,
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    '&:hover': {
                      background: "linear-gradient(45deg, #6209A1 30%, #8E24AA 90%)",
                    },
                    '&:disabled': {
                      background: "#ccc",
                      color: "#666"
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Change Password"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Card>
      )}
      <ToastContainer />
    </Box>
  );
};

export default Profile;
